import { DOCUMENT } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AdminButtonComponent, AdminIconComponent } from '../../../shared/admin';
import { CrmAssignedTasksTableComponent } from '../../../shared/components/crm-assigned-tasks/crm-assigned-tasks-table.component';
import {
  buildTaskReminderMessage,
  isUrgentAssignedTask,
  type PlatformTaskReminder,
} from '../../../shared/models/assigned-task.models';
import { PlatformDashboardService } from '../../../shared/services/platform-dashboard.service';
import { TaskReminderRefreshService } from '../../../shared/services/task-reminder-refresh.service';

const DISMISS_STORAGE_KEY = 'cg_platform_task_reminder_dismissed';

@Component({
  selector: 'app-dashboard-task-reminder-banner',
  standalone: true,
  imports: [
    AdminButtonComponent,
    AdminIconComponent,
    CrmAssignedTasksTableComponent,
  ],
  templateUrl: './dashboard-task-reminder-banner.component.html',
  styleUrl: './dashboard-task-reminder-banner.component.scss',
})
export class DashboardTaskReminderBannerComponent implements OnInit {
  private readonly doc = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dashboardService = inject(PlatformDashboardService);
  private readonly refreshService = inject(TaskReminderRefreshService);
  private readonly rootRef = viewChild<ElementRef<HTMLElement>>('bannerRoot');

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly reminder = signal<PlatformTaskReminder | null>(null);
  readonly dismissed = signal(false);
  readonly panelOpen = signal(false);

  readonly hasUrgentTasks = computed(() => {
    const data = this.reminder();
    if (!data) return false;
    return data.dueTodayCount > 0 || data.overdueCount > 0;
  });

  readonly visible = computed(() => {
    return !this.loading() && !this.error() && this.hasUrgentTasks() && !this.dismissed();
  });

  readonly messageHtml = computed(() => {
    const data = this.reminder();
    if (!data) return '';

    const dueToday = data.dueTodayCount;
    const overdue = data.overdueCount;
    const parts: string[] = [];

    if (dueToday > 0) {
      parts.push(`<strong>${dueToday}</strong> task${dueToday === 1 ? '' : 's'} due today`);
    }

    if (overdue > 0) {
      parts.push(`<strong>${overdue}</strong> overdue task${overdue === 1 ? '' : 's'}`);
    }

    if (parts.length === 0) {
      return buildTaskReminderMessage(0, 0);
    }

    return `You have ${parts.join(' and ')}. Please review and complete them on time.`;
  });

  readonly urgentTasks = computed(() =>
    (this.reminder()?.urgentTasks ?? []).filter(isUrgentAssignedTask),
  );

  ngOnInit(): void {
    this.loadReminder();

    this.refreshService.onRefresh$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadReminder());

    fromEvent<MouseEvent>(this.doc, 'click')
      .pipe(
        filter(() => this.panelOpen()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        const root = this.rootRef()?.nativeElement;
        if (root && !root.contains(event.target as Node)) {
          this.panelOpen.set(false);
        }
      });

    fromEvent<KeyboardEvent>(this.doc, 'keydown')
      .pipe(
        filter((event) => event.key === 'Escape' && this.panelOpen()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.panelOpen.set(false));
  }

  onViewTasksClick(event: MouseEvent): void {
    event.stopPropagation();
    this.panelOpen.update((open) => !open);
  }

  dismiss(): void {
    const data = this.reminder();
    if (data) {
      this.persistDismissal(data);
    }
    this.dismissed.set(true);
    this.panelOpen.set(false);
  }

  private loadReminder(): void {
    this.loading.set(true);
    this.error.set(null);

    this.dashboardService.getTaskReminder().subscribe({
      next: (data) => {
        this.reminder.set(data);
        this.dismissed.set(this.isDismissed(data));
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Unable to load task reminders.');
        this.loading.set(false);
      },
    });
  }

  private dismissSignature(data: PlatformTaskReminder): string {
    return `${data.dueTodayCount}:${data.overdueCount}`;
  }

  private isDismissed(data: PlatformTaskReminder): boolean {
    if (typeof sessionStorage === 'undefined') return false;
    return sessionStorage.getItem(DISMISS_STORAGE_KEY) === this.dismissSignature(data);
  }

  private persistDismissal(data: PlatformTaskReminder): void {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(DISMISS_STORAGE_KEY, this.dismissSignature(data));
  }
}
